<script setup>
import {onMounted, onUnmounted, ref, watch, computed} from "vue";
import {useSearchStore} from "@/stores/searchStore";
import {useTripsStore} from "@/stores/tripsStore";
import {useUserStore} from "@/stores/userStore";
import { useYachtStore } from "@/stores/yachtStore";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import { nextTick } from 'vue';
import { useAuth0 } from "@auth0/auth0-vue";
import { format as fmt } from '@/helpers/dateUtils';
import { useFormatStore } from '@/stores/formatStore';
import boat1 from "@/assets/about/boat-1.png";
import TripRow from "@/components/TripRow/TripRow.vue";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import CustomButton from "@/components/Button/Button.vue";
const defaultImage = boat1;
const formatStore = useFormatStore();


const userStore = useUserStore();
const tripsStore = useTripsStore();
const searchStore = useSearchStore();
const yachtStore = useYachtStore();


const {t} = useI18n();

const router = useRouter();
const route = useRoute();
const { loginWithRedirect } = useAuth0();


const rememberAndLogin = () => {
  try {
    const path = route.fullPath || '/';
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('postLoginRedirect', path);
    }
    loginWithRedirect({ appState: { targetUrl: path } });
  } catch (e) {
    console.warn('Failed to persist redirect path before login', e);
    loginWithRedirect();
  }
};

const styledTrips = computed(() => {
  const selStart = props.dateFrom ? fmt(props.dateFrom) : '';
  const selEnd = props.dateTo ? fmt(props.dateTo) : '';
  const datesProvided = !!selStart && !!selEnd;

  const trips = tripsStore.getAvailable().map(trip => {
    const tripRegionId = String(trip.locationId?.region?.id ?? trip.locationId?.id ?? '');
    const yachtRegionId = props.yachtLocation;

    const regionMatches =
        tripRegionId !== '' &&
        yachtRegionId !== '' &&
        tripRegionId === String(yachtRegionId);

    const tripStart = String(trip.dateStart).slice(0, 10);
    const tripEnd = String(trip.dateEnd).slice(0, 10);
    const datesMatch = !datesProvided || (tripStart === selStart && tripEnd === selEnd);

    const isCompatible = regionMatches && datesMatch;
    const isSelected = isYachtInTrip(trip) || trip.uuid === clickedTripUuid.value;

    const status = trip.state;
    let background = '';
    switch (status) {
      case 'open': background = '#9be7b9'; break;
      case 'offer': background = '#9be7b9'; break;
      case 'active': background = '#9be7b9'; break;
      case 'done': background = '#9be7b9'; break;
      case 'cancelled': background = '#ff9d9d'; break;
      case 'planned': background = '#80beff'; break;
      default: background = 'black'; break;
    }

    return { ...trip, style: { background }, status, isCompatible, isSelected };
  });

  // Sort: selected first, then compatible first, then newest
  return trips.sort((a, b) => {
    if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
    if (a.isCompatible !== b.isCompatible) return a.isCompatible ? -1 : 1;
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    return 0;
  });
});





const isBoatInAnyVisibleTrip = computed(() => {
  const id = currentYachtId.value;
  if (id == null) return false;
  return styledTrips.value.some(trip => tripsStore.isYachtInTripByUuid(trip.uuid, id));
});

const userSelected = ref(false);
const offerPatched = ref(false);
const clickedTripUuid = ref(null);
const showPopup = ref(false);
const addingSlug = ref(null);
const myId = computed(() => props.yacht?.yacht?.id ?? props.yacht?.id);
const currentYachtId = computed(() => props.yacht?.yacht?.id ?? props.yacht?.id ?? null);


//This is the function that is called when the user clicks on the AddToFavoriteButton
function handleToggle() {
  if (!showPopup.value) {
    document.dispatchEvent(new CustomEvent('close-all-add-favorite', { detail: myId.value }));
    showPopup.value = true;
  } else {
    showPopup.value = false;
  }
}

// This closes all AddToFavoriteButtons except the one that triggered the event
function onCloseAll(e) {
  if (e?.detail !== myId.value) {
    showPopup.value = false;
  }
}


// Choose a trip slug to navigate to: prefer the last-clicked one, otherwise
// any visible trip that already contains this yacht.
const goToTripUuid = computed(() => {
  if (clickedTripUuid.value) return clickedTripUuid.value;
  const id = currentYachtId.value;
  const found = tripsStore.getAvailable().find(trip =>
    tripsStore.isYachtInTripByUuid(trip.uuid, id)
  );
  return found?.uuid || null;
});

function goToTrip() {
  const uuid = goToTripUuid.value;
  if (uuid) {
    router.push(`/my/trip/${uuid}`);
  }
}

const props = defineProps({
  yacht: Object,
  amenities: {
    type: Array,
    default: () => []
  },
  selectedAmenities: {
    type: Array,
    default: () => []
  },
  passengers:{
    type: Number
  },
  discount: {
    type: Number
  },
  popupSize: {
    type: String,
    default: 'default'
  },
  showAddTripModal: Boolean,
  dateFrom: {
    type: [String, Date],
    default: null
  },
  dateTo: {
    type: [String, Date],
    default: null
  },
  yachtLocation: {
    type: [String, Object, Number, Array],
    default: null
  }
});

const emit = defineEmits([
  'update:showAddTripModal',
]);

const showAddTripModal = computed({
  get: () => props.showAddTripModal,
  set: (value) => emit('update:showAddTripModal', value),
});


onMounted(async () => {
  if (userStore.isLoggedIn && !tripsStore.hasTrips()) {
    await tripsStore.getAll();
  }
  document.addEventListener('close-all-add-favorite', onCloseAll);
  if (userStore.isLoggedIn) {
    await tripsStore.ensureLoaded?.();
  }
});

// Reset clickedTripUuid whenever filters or the yacht change so stale selection doesn’t keep the heart blue
watch([() => props.dateFrom, () => props.dateTo, currentYachtId], () => {
  clickedTripUuid.value = null;
});


function isYachtInTrip(trip) {
  console.log("Is yacht in trip",tripsStore.isYachtInTripByUuid(trip.uuid, currentYachtId.value))
  return tripsStore.isYachtInTripByUuid(trip.uuid, currentYachtId.value);
}

// Make clicking the selected (blue) row navigate to the trip
function onTripRowClick(trip) {
  const selected = isYachtInTrip(trip) || trip.uuid === clickedTripUuid.value;
  if (selected) {
    clickedTripUuid.value = trip.uuid;
    goToTrip();
  } else {
    handleTripClick(trip.uuid);
  }
}

onUnmounted(() => {
  document.removeEventListener('close-all-add-favorite', onCloseAll);
});

// Ensure discount is a single number
function normalizeDiscount(input) {
  if (input == null) return null;
  let val = input;
  if (Array.isArray(val)) {
    val = val[0];
  }
  if (typeof val === 'string') {
    val = val.replace('%', '').trim();
  }
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
}

function buildOfferPayload() {
  return {
    yachtId: props.yacht?.yacht?.id || props.yacht?.id,
    name: props.yacht?.yacht?.name || props.yacht?.name,
    basePrice: props.yacht?.price,
    discount: normalizeDiscount(props.discount),
    offerPrice: props.yacht?.finalPrice,
    dateStart: formatStore.formatDate(new Date(props.dateFrom)),
    dateEnd: formatStore.formatDate(new Date(props.dateTo)),
    destination: props.yachtLocation,
    amenities: props.amenities,
    selectedAmenities: props.selectedAmenities,
    passengers: props.passengers || 0,
  };
}

const isYachtPage = computed(() => route.name === 'YachtPage' || route.meta?.yachtPage === true);


async function handleTripClick(uuid) {
  if (addingSlug.value) return; // prevent double submits while busy
  addingSlug.value = uuid;
  try {
    let optionalAmenities = [];

    if (isYachtPage.value) {

      console.log("Inside yacht page")
      // if we are in the yacht page take amenities from availability
      optionalAmenities = yachtStore.optionalAmenities;
    }
    else {
      console.log("Outside yacht page")
      // if we are out of the yacht page, take amenities from the yacht
      const amenitiesData = await yachtStore.fetchAmenitiesForFavorites(props.yacht.yacht.slug)
      optionalAmenities = amenitiesData.optional
    }

    const offerPayload = {
      ...buildOfferPayload(),
      amenities: optionalAmenities 
    };

    await tripsStore.patchOfferToTrip(uuid, offerPayload);

    offerPatched.value = !offerPatched.value;
    clickedTripUuid.value = uuid;
    showPopup.value = false;
    searchStore.selectedBoat = null;
  } catch (e) {
    console.error('[AddToFavorite] Failed to add yacht to trip:', e);
  } finally {
    addingSlug.value = null;
  }
}


function removeSelectedClass() {
  userSelected.value = false;
  searchStore.selectedBoat = null;
}


//This gets the classes for the trip based on if it is selected or not
function getTripClasses(trip) {
  const base = 'flex items-center gap-3 responsive-body-small px-2.5 py-2 rounded-6xl cursor-pointer hover:bg-bw-lightGrey transition-colors duration-300';
  const selected = 'bg-primary-blue text-white selected-offer__border selected-offer__row hover:text-black';
  const loadingCls = addingSlug.value === trip.uuid ? 'opacity-70 pointer-events-none' : '';
  const isSelected = isYachtInTrip(trip) || trip.uuid === clickedTripUuid.value;
  return isSelected ? `${base} ${selected} ${loadingCls}` : `${base} ${loadingCls}`;
}
</script>

<template>
  <el-popover
    placement="top"
    :width="(isBoatInAnyVisibleTrip || clickedTripUuid) ? 110 : 80"
    trigger="hover"
    :popper-style="{ 'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700' }"
    :popper-class="(isBoatInAnyVisibleTrip || clickedTripUuid) ? 'text-black' : 'text-black pointer-events-none'"
  >
    <template #reference>
      <button
        @click="handleToggle"
        :class="[
          'add-boat__btn border z-10 transition-colors px-4 py-1.5 rounded-12xl h-auto cursor-pointer',
          (isBoatInAnyVisibleTrip || clickedTripUuid) ? 'border-primary-blue' : 'border-grey'
        ]"
      >
        <img
          :class="(isBoatInAnyVisibleTrip || clickedTripUuid) ? 'comparison-icons-selected' : ''"
          src="@/assets/icons/svg/heart-icon.svg"
          alt="add to trip"
        />
      </button>
    </template>

    <template v-if="isBoatInAnyVisibleTrip || clickedTripUuid">
      <button
        class="text-black font-bold"
        style="font-size: .75rem"
        :aria-label="t('offerIsSaved')"
      >
        {{ t('offerIsSaved') }}
      </button>
    </template>
    <template v-else>
      <div>{{ t('saveOffer') }}</div>
    </template>
  </el-popover>

  <div
    @mouseover="removeSelectedClass"
    :class="[
          'add-to-favorite-popup flex flex-col absolute z-10 p-2 bg-white rounded-6xl drop-shadow-popup max-h-60',
          showPopup ? '' : 'hidden',
          userSelected ? 'selected-offer__border' : '',
          props.popupSize === 'default' 
          ? 'w-[150%] -translate-x-[30%] translate-y-[20%]' 
          : props.popupSize === 'medium'
          ? 'w-[65%] -translate-x-[20%] translate-y-[30%]'
          : 'w-[170%] -translate-x-[35%] translate-y-[30%]'
        ]"
  >
    <div class="flex-1 overflow-y-auto flex flex-col gap-2">
      <div class="flex flex-column" v-for="trip in styledTrips.slice(0,5)" :key="trip.uuid + offerPatched">
        <template v-if="!trip.isCompatible">
          <el-popover
              placement="top"
              trigger="hover"
              effect="customized"
          >
            <template #reference>
              <div
                  @mouseenter="hovering = true"
                  @mouseleave="hovering = false"
                  :class="['relative', !trip.isCompatible ? 'opacity-50 cursor-not-allowed' : '']"
              >
                <TripRow
                    :trip="trip"
                    :addingSlug="addingSlug"
                    :defaultImage="defaultImage"
                    :class="[getTripClasses(trip), !trip.isCompatible ? 'opacity-50 cursor-not-allowed' : '']"
                />
              </div>
            </template>
  
            <!-- Popover slot content -->
            <div class="flex items-center gap-2">
              <img src="@/assets/icons/svg/Action_required_16x16.svg" alt="warning" class="w-4 h-4"/>
              <span>{{ t('sameDateHarbor') }}</span>
            </div>
          </el-popover>
  
        </template>
  
        <template v-else>
          <div
              v-if="trip.isCompatible"
              @click="onTripRowClick(trip)"
              :aria-busy="addingSlug === trip.uuid"
              :aria-label="(isYachtInTrip(trip) || trip.uuid === clickedTripUuid)
                ? (t('goToTrip') || 'Go to Trip')
                : (t('saveOfferToThisTrip') || 'Save offer to this trip')"
          >
            <TripRow :trip="trip" :addingSlug="addingSlug" :defaultImage="defaultImage" :class="getTripClasses(trip)"/>
          </div>
        </template>
      </div>
    </div>

    <div class="sticky bottom-0 bg-white pt-2">
      <div v-if="userStore.isLoggedIn">
        <div @click="router.push('/my/trips')" class="flex items-center gap-4 responsive-body-small px-2.5 py-2 rounded-6xl cursor-pointer hover:bg-bw-lightGrey transition-[background]">
          <img class="bg-gray-100 p-1 rounded-full w-6" src="@/assets/icons/svg/More.svg" alt="See all">
          <div class="font-semibold">{{ t('seeAllTrips') }}</div>
        </div>
        <div
           @click="() => {
             showAddTripModal = true;
             nextTick(() => { showPopup = false; });
           }"
           class="flex items-center gap-4 responsive-body-small px-2.5 py-2 rounded-6xl cursor-pointer hover:bg-bw-lightGrey transition-[background]"
        >
          <img class="bg-gray-100 p-1 rounded-full w-6" src="@/assets/icons/svg/Add_24x24.svg" alt="add-icon">
          <div class="font-semibold">{{ t('createNewTrip') }}</div>
        </div>
      </div>
      <div v-else
           @click="rememberAndLogin"
           class="flex items-center gap-4   responsive-body-small px-2.5 py-2 rounded-6xl cursor-pointer hover:bg-bw-lightGrey transition-[background]">
        <img class="bg-gray-100 p-1 rounded-full w-6" src="@/assets/icons/svg/Guest_24x24.svg"
             alt="add-icon">
        <div class="font-semibold">{{ t('loginToSaveOffer') }}</div>
      </div>
    </div>

  </div>

</template>

<style scoped>
.users-selected-boat .add-to-favorite-popup {
  display: flex !important;
}
.users-selected-boat .selected-offer__border {
  outline: solid 2px #3B82F6 !important;
}
.border-primary-blue {
  border-color: #3B82F6 !important;
  background: #3B82F6 !important;
}
.border-grey:hover {
  border-color: #000000 !important;
}
.comparison-icons-selected {
  filter: invert(1);
}

/* Ensure selected row text turns black on hover (overrides inner text colors) */
.selected-offer__row:hover,
.selected-offer__row:hover * {
  color: black !important;
}

/* Make dates black whenever the parent row is selected (blue) */
.selected-offer__row .trip-dates {
  color: black !important;
}
</style>